// SalesAndOps.jsx — Unified Sales page.
// Merges the historical "نظرة عامة تشغيلية" + "تفاصيل المبيعات والأصناف"
// into ONE continuous page with clear KPI hero at the top and the
// detailed drill-downs below. The duplicated "صحة الشركة" bar is removed
// (already shown on the main dashboard).
import React, { useMemo } from "react";
import { Overview } from "./Dashboard";
import SalesPage from "./SalesPage";

const fm = (n, d = 0) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: d });

function KpiHero({ label, value, sub, color, testid, accent }) {
  return (
    <div data-testid={testid} style={{
      padding: 16, borderRadius: 14, background: "#fff",
      border: `1px solid var(--border)`,
      boxShadow: "0 1px 0 rgba(0,0,0,.02)",
      borderInlineStart: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700, letterSpacing: 0.3 }}>{label}</div>
      <div style={{
        fontSize: 26, fontWeight: 900, color: "var(--brown-deep)",
        letterSpacing: -0.5, marginTop: 4, lineHeight: 1.1,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: accent || "var(--muted)", marginTop: 4, fontWeight: accent ? 700 : 500 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function SalesAndOps({ fil, cst, dsum, period, dateFrom, dateTo }) {
  // Compute clean KPIs from invoices (fil) — same calc the Overview uses but
  // exposed at the top in a more compact, executive-friendly bar.
  const kpis = useMemo(() => {
    const rows = fil || [];
    if (!rows.length) {
      return { total: 0, count: 0, aov: 0, days: 0, bestDay: null, paidPct: 0 };
    }
    const total = rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
    const paid = rows.filter((r) => r.isPaid).length;
    const byDay = {};
    rows.forEach((r) => {
      const d = (r.date || "").slice(0, 10);
      if (!d) return;
      byDay[d] = (byDay[d] || 0) + (Number(r.total) || 0);
    });
    const days = Object.keys(byDay).length;
    const aov = paid > 0 ? total / paid : 0;
    const bestEntry = Object.entries(byDay).sort((a, b) => b[1] - a[1])[0];
    const paidPct = rows.length ? (paid / rows.length) * 100 : 0;
    return {
      total, count: rows.length, paid, days, aov,
      bestDay: bestEntry ? { date: bestEntry[0], value: bestEntry[1] } : null,
      paidPct,
      avgDaily: days > 0 ? total / days : 0,
    };
  }, [fil]);

  // Sub-totals from POS dsum if present (more authoritative than invoices)
  const posTotal = useMemo(() => {
    if (!dsum || !dsum.length) return null;
    return dsum.reduce((s, r) => s + (Number(r.net_with_tax) || Number(r.net) || Number(r.gross) || 0), 0);
  }, [dsum]);

  return (
    <div data-testid="sales-ops-page" style={{ display: "grid", gap: 14 }}>
      {/* Hero header */}
      <div data-testid="sales-hero" style={{
        padding: "20px 22px", borderRadius: 14,
        background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 60%, #b08756 120%)",
        color: "#fff", boxShadow: "0 6px 18px rgba(107,78,61,.25)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 1.2, fontWeight: 700 }}>SALES</div>
            <h2 style={{ margin: "4px 0 2px", fontSize: 26, fontWeight: 900, letterSpacing: -0.5 }}>
              💰 المبيعات — نظرة شاملة
            </h2>
            <div style={{ fontSize: 12.5, opacity: 0.9 }}>
              مؤشرات الأداء الرئيسية، تفاصيل الفواتير، أفضل المنتجات، تحليل الورديات والمقارنات الزمنية — صفحة واحدة.
            </div>
          </div>
          <div style={{ display: "grid", gap: 4, textAlign: "left", fontSize: 12, opacity: 0.92 }}>
            <div>الفترة: <b>{kpis.days}</b> يوم</div>
            <div><b>{fm(kpis.count)}</b> فاتورة مسجّلة</div>
            {posTotal != null && (
              <div style={{ fontSize: 11, opacity: 0.8 }}>POS: <b>{fm(posTotal)}</b> ر</div>
            )}
          </div>
        </div>
      </div>

      {/* Executive KPI bar */}
      <div data-testid="sales-kpi-bar" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        gap: 12,
      }}>
        <KpiHero
          testid="sales-kpi-total"
          label="💰 إجمالي المبيعات"
          value={`${fm(kpis.total)} ر`}
          sub={`من ${fm(kpis.count)} فاتورة`}
          color="#10B981"
        />
        <KpiHero
          testid="sales-kpi-avg"
          label="🧾 متوسط الفاتورة"
          value={`${fm(kpis.aov, 1)} ر`}
          sub="من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية"
          color="#0EA5E9"
        />
        <KpiHero
          testid="sales-kpi-daily"
          label="📅 متوسط مبيعات اليوم"
          value={`${fm(kpis.avgDaily)} ر`}
          sub={`على مدى ${kpis.days} يوم`}
          color="#D97706"
        />
        <KpiHero
          testid="sales-kpi-best"
          label="🏆 أفضل يوم"
          value={kpis.bestDay ? `${fm(kpis.bestDay.value)} ر` : "—"}
          sub={kpis.bestDay?.date || "بانتظار البيانات"}
          color="#7C3AED"
          accent="var(--success)"
        />
      </div>

      {/* Overview block (without the duplicated HealthKpiBar) */}
      <Overview fil={fil} cst={cst} dsum={dsum} period={period} dateFrom={dateFrom} dateTo={dateTo} hideHealth={true} />

      {/* Divider before details */}
      <div data-testid="sales-divider" style={{
        margin: "8px 0", padding: "10px 16px", borderRadius: 10,
        background: "linear-gradient(90deg, var(--beige-soft), transparent)",
        borderInlineStart: "4px solid var(--brown)", display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 18 }}>📦</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)" }}>تفاصيل المبيعات والأصناف</div>
          <div style={{ fontSize: 11.5, color: "var(--muted)" }}>عرض يومي/أسبوعي/شهري · مقارنة فترتين · تحليل الورديات · تصدير CSV</div>
        </div>
      </div>

      {/* Sales detailed drill-down */}
      <SalesPage fil={fil} dsum={dsum} />
    </div>
  );
}
